import jsonata from 'jsonata';
import * as _ from 'lodash';

import {
  createSortComparer,
  DiffTypeBasic,
  ReducingOperation,
  ReducingOperationBasic,
  SortDirection,
  SortDirectionBasic
} from '../common/array';
import { InvalidReducerOperation } from '../errors';

import { isArray } from './values';

/**
 * Get the item of an array at a specific index.
 * @param arr The array containing the item to retrieve.
 * @param index The position of the item to retrieve from the array.
 *
 * @returns `T` The item retrieved from the array.
 */
export function getAt<T>(arr: T[], index: number): T {
  if (index < 0) index = arr.length + index;
  return arr[index];
}

/**
 * Get the subarray of items that exist between the specified starting and ending indices of an array.
 * @param arr The array containing the items to retrieve.
 * @param start The starting index of the range to extract from the array.
 * @param end The ending index of the range to extract from the array.
 *
 * @returns `Array` A new array containing the items located between the specified starting and ending indices of the input array.
 */
export function slice<T>(arr: T[], start: number, end: number): T[] {
  return arr.slice(start, end);
}

/**
 * Remove all instances of an item from an array.
 * @param arr An array containing the item to remove.
 * @param item The item to remove from the array.
 *
 * @returns `Array` The modified array after removing the item at the given index.
 */
export function removeItem<T>(arr: T[], item: T): T[] {
  return arr.filter((value: T) => value !== item);
}

/**
 * Get an array containing the value or array of values of the second input followed by the value or array of values of the first input.
 * @param value The value or array of values to prepend with `prependValue`.
 * @param prependValue The value or array of values to prepend to `value`.
 *
 * @returns `Array` A new array containing value or array of values of the second input followed by the value or array of values of the first input.
 */
export function prepend<L, R>(value: L | L[], prependValue: R | R[]): Promise<(L | R)[]> {
  const data = {
    value,
    prependValue
  };
  return jsonata('$append(prependValue, value)').evaluate(data) as Promise<(L | R)[]>;
}

/**
 * Return the difference between two arrays based on the type of diff specified.
 * @param first The first array.
 * @param second The second array.
 * @param diffType Whether the returned array should contain the items from the first, second, or both arrays.
 *
 * @returns `Array` An array containing the items of one array that do not exist in the other array based on the type of diff specified.
 */
export function diffBasic<T, DT extends `${DiffTypeBasic}`>(
  first: T[],
  second: T[],
  diffType: DiffTypeBasic | DT
): T[] {
  switch (diffType) {
    case DiffTypeBasic.leftDiff:
      return first.filter(item => !second.includes(item));
    case DiffTypeBasic.rightDiff:
      return second.filter(item => !first.includes(item));
    default:
      return [...first.filter(item => !second.includes(item)), ...second.filter(item => !first.includes(item))];
  }
}

/**
 * Remove duplicate items from an array.
 * @param arr The array containing the duplicate values.
 *
 * @returns `Array` A new array containing unique values.
 */
export function removeDuplicates<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

/**
 * Wrap a value in an array if it is not already an array.
 * @param value The array or value to wrap in array.
 *
 * @returns `Array` An array obtained by wrapping the input item in array.
 */
export function force<T>(value: T | T[]): T[] {
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
}

/**
 * Flatten an array containing potentially nested arrays to a single array.
 * @param arr The array to flatten.
 *
 * @returns `Array` The flattened array.
 */
export function flatten<T>(arr: T[]): (T extends readonly (infer InnerArr)[] ? InnerArr : T)[] {
  return arr.flat();
}

/**
 * Extract the values of a property or deeply nested property of the objects within an array of objects.
 * @param arr The array of objects from which to pluck values.
 * @param path The JSONata property path of the values to pluck.
 *
 * @returns `Array` An array containing the extracted values.
 */
export async function pluckBy<T, R>(arr: T[], path: string): Promise<R[]> {
  const result: R[] = await jsonata(path).evaluate(arr);
  if (result) {
    return [...force(result)];
  }
  return [];
}

/**
 * Sort the items of an array of strings or numbers by value.
 * @param arr The array to sort by value.
 * @param direction The sort direction.
 * @param localeLanguage The locale language to use when comparing values.
 *
 * @returns `Array` The sorted array.
 */
export function sort<S extends `${SortDirection}`, SB extends `${SortDirectionBasic}`>(
  arr: unknown[],
  direction: SortDirection | S | SB = SortDirection.asc,
  localeLanguage = 'en'
): unknown[] {
  if (!isArray(arr)) {
    return arr;
  }

  const comparer = createSortComparer(direction, localeLanguage);
  return arr.sort(comparer);
}

/**
 * Sort the items of an array of objects by the value of a plucked property of the objects within the array.
 * @param arr The array of objects to sort.
 * @param path The JSONata property path of the values of the objects to compare.
 * @param direction The sort direction.
 * @param localeLanguage The locale language to use when comparing values.
 *
 * @returns `Array` A new array sorted depends on the path and the type.
 */
export function sortBy<T, S extends `${SortDirection}`, SB extends `${SortDirectionBasic}`>(
  arr: { [path: string]: T }[],
  path: string,
  direction: SortDirection | SortDirectionBasic | S | SB = SortDirection.asc,
  localeLanguage = 'en'
): Promise<unknown[]> {
  if (!isArray(arr)) {
    return Promise.resolve(arr);
  }

  const sortExpression = `$sort($, function($left, $right) { $compare($eval("${path}", $left), $eval("${path}", $right)) = 1 } )`;
  const comparer = createSortComparer(direction, localeLanguage);

  return jsonata(sortExpression).evaluate(arr, { compare: comparer }) as Promise<unknown[]>;
}

/**
 * Count all items of an array that are equal a specific value.
 * @param arr The array of values to compare and count.
 * @param value The value to count.
 *
 * @returns The count of all items equal to the specified value.
 */
export function countIf<T>(arr: T[], value: unknown): number {
  return arr.filter(item => item == value).length;
}

/**
 * Count all items of an array of objects that contain a plucked property equal to a specific value.
 * @param arr The array of objects to count.
 * @param path The JSONata property path of the values of the objects to compare and count.
 * @param value The value to count.
 *
 * @returns The count of all objects containing a property equal to the specified value.
 */
export async function countIfBy<T>(arr: { [path: string]: T }[], path: string, value: string): Promise<number> {
  const values = await pluckBy(arr, path);
  return countIf(values, value);
}

/**
 * Apply a reducing operation to the values of a numeric array.
 * @param arr The array of items to reduce.
 * @param operation The reducing operation to perform.
 *
 * @returns The reduced value.
 */
export function reduce<O extends `${ReducingOperation}`, OB extends `${ReducingOperationBasic}`>(
  arr: number[],
  operation: ReducingOperation | ReducingOperationBasic | O | OB
): number | undefined {
  const numericArray = arr.filter(i => !isNaN(i));
  switch (operation) {
    case ReducingOperation.avg:
    case ReducingOperationBasic.averaging:
      return _.mean(numericArray);
    case ReducingOperation.max:
    case ReducingOperationBasic.findingMaximum:
      return _.max(numericArray);
    case ReducingOperation.min:
    case ReducingOperationBasic.findingMinimum:
      return _.min(numericArray);
    case ReducingOperation.sum:
    case ReducingOperationBasic.summing:
      return _.sum(numericArray);
  }

  throw new InvalidReducerOperation();
}

/**
 * Apply a reducing operation to the values of a plucked property of the objects within an array of objects.
 * @param arr The array of objects to reduce.
 * @param operation The reducing operation to perform.
 * @param path The JSONata property path of the values to reduce.
 *
 * @returns The reduced value.
 */
export async function reduceBy<T, O extends `${ReducingOperation}`, OB extends `${ReducingOperationBasic}`>(
  arr: T[],
  operation: ReducingOperation | ReducingOperationBasic | O | OB,
  path: string
): Promise<number | undefined> {
  const values: number[] = await pluckBy(arr, path);
  return reduce(values, operation);
}

/**
 * Sum the values of an array of numbers.
 * @param arr The array containing the values to sum.
 *
 * @returns The sum of the values.
 */
export function sum(arr: number[]): number | undefined {
  return reduce(arr, ReducingOperation.sum);
}

/**
 * Sum the values of a plucked property of the objects within an array of objects.
 * @param arr The array of objects containing the values to sum.
 * @param path The JSONata property path of the values to sum.
 *
 * @returns The sum of the property values.
 */
export function sumBy<T>(arr: T[], path: string): Promise<number | undefined> {
  return reduceBy(arr, ReducingOperation.sum, path);
}

/**
 * Average the values of an array of numbers.
 * @param arr The array containing the values to average.
 *
 * @returns The average value.
 */
export function avg(arr: number[]): number | undefined {
  return mean(arr);
}

/**
 * Average the values of a plucked property of the objects within an array of objects.
 * @param arr The array of objects containing the values to average.
 * @param path The JSONata property path of the values to average.
 *
 * @returns Return the avg number.
 */
export function avgBy<T>(arr: T[], path: string): Promise<number | undefined> {
  return meanBy(arr, path);
}

/**
 * Find the minimum value in an array of numbers.
 * @param arr The array containing the values to search.
 *
 * @returns The minimum value found.
 */
export function min(arr: number[]): number | undefined {
  return reduce(arr, ReducingOperation.min);
}

/**
 * Find the minimum value of a plucked property of the objects within an array of objects.
 * @param arr The array of objects containing the values to search.
 * @param path The JSONata property path of the values to search.
 *
 * @returns The minimum value found.
 */
export function minBy<T>(arr: T[], path: string): Promise<number | undefined> {
  return reduceBy(arr, ReducingOperation.min, path);
}

/**
 * Find the maximum value in an array of numbers.
 * @param arr The array containing the values to search.
 *
 * @returns The maximum value found.
 */
export function max(arr: number[]): number | undefined {
  return reduce(arr, ReducingOperation.max);
}

/**
 * Find the maximum value of a plucked property of the objects within an array of objects.
 * @param arr The array of objects containing the values to search.
 * @param path The JSONata property path of the values to search.
 *
 * @returns The maximum value found.
 */
export function maxBy<T>(arr: T[], path: string): Promise<number | undefined> {
  return reduceBy(arr, ReducingOperation.max, path);
}

/**
 * Compute the mean of the values in an array of numbers.
 * @param arr The array containing the values to average.
 *
 * @returns The mean value.
 */
export function mean(arr: number[]): number {
  return _.mean(arr.filter(i => !isNaN(i)));
}

/**
 * Compute the mean of the values plucked from objects within an array of objects.
 * @param arr The array of objects containing the values to average.
 * @param path The JSONata property path of the values to search.
 *
 * @returns The mean value.
 */
export function meanBy<T>(arr: T[], path: string): Promise<number | undefined> {
  return reduceBy(arr, ReducingOperation.avg, path);
}

/**
 * Filter an array to the values that match a regular expression, a specific value, or non-null value.
 *
 * @param arr The array to filter.
 * @param filterValue The filter value or regular expression to match.
 *
 * @returns The filtered values.
 */
export function filter<T>(arr: T[], filterValue?: string | T): T[] {
  if (!isArray(arr)) {
    return [];
  }

  let regex: RegExp | undefined;
  if (filterValue && typeof filterValue === 'string') {
    regex = new RegExp(filterValue);
    if (!_.isRegExp(regex)) {
      regex = undefined;
    }
  }

  return arr.filter(item => {
    if (regex && typeof item === 'string') {
      return regex.test(item);
    } else if (filterValue) {
      return item == filterValue;
    }
    return item != null && (filterValue === null || filterValue === undefined);
  });
}

/**
 * Filter an array of objects to those that contain a property or deeply nested property whose values match a regular
 * expression, a specific value, or non-null value.
 *
 * @param arr The array to filter.
 * @param path The JSONata property path of the values to compare.
 * @param filterValue The filter value or regular expression to match.
 *
 * @returns The filtered objects that contain a property or deeply nested property whose values match the filter value.
 */
export async function filterBy<T>(arr: T[], path: string, filterValue?: string | T): Promise<T[]> {
  if (!path || !isArray(arr)) {
    return [];
  }

  let regex: RegExp | undefined;
  if (filterValue && typeof filterValue === 'string') {
    regex = new RegExp(filterValue);
    if (!_.isRegExp(regex)) {
      regex = undefined;
    }
  }

  const filterExpression = `$filter($, function($item){ $eval("${path}", $item) ~> $isMatch })`;
  const isMatch = (value: unknown): boolean => {
    if (value === undefined) {
      return false;
    }

    if (regex && typeof value === 'string') {
      return regex.test(value);
    } else if (filterValue) {
      return value == filterValue;
    }
    return value != null && (filterValue === null || filterValue === undefined);
  };

  const result = (await jsonata(filterExpression).evaluate(arr, { isMatch: isMatch })) as T[];
  if (result) {
    return [...force(result)];
  }
  return [];
}
